
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

import Loader from '@/common/components/UI/Loader/index.vue'
import NewTable from '@/ui-kit/NewTable/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'

import { rpp } from '@/core/realisations/rpp.realisation'

import { LtlRppDetails, LtlRppComment } from '@/core/types/rpp.types'

import useNotifications from '@/common/composable/useNotifications'
import { computed } from 'vue'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

const COLUMNS = [
    {
        key: 'invoiceId',
        name: 'Нумерация',
    },
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'payerCompany',
        name: 'Плательщик',
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'cubature',
        name: 'Кубатура',
    },
    {
        key: 'weight',
        name: 'Вес',
    },
    {
        key: 'cost',
        name: 'Стоимость доставки',
    },
    {
        key: 'costWithoutNds',
        name: 'Доставка без НДС',
    },
    {
        key: 'totalCost',
        name: 'Итого',
    },
]

const COMMENTS_COLUMNS = [
    {
        key: 'name',
        name: 'Имя',
    },
    {
        key: 'role',
        name: 'Должность',
    },
    {
        key: 'costOfTransportation',
        name: 'Запрашиваемая стоимость',
    },
    {
        key: 'status',
        name: 'Ответ',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'stage',
        name: 'Этап',
    },
]

export default defineComponent({
    components: {
        Loader,
        NewTable,
        PageTitle,
        Cheap,
        NewButton,
        ButtonDropdown,
        TheTextarea,
        NewModal,
    },
    setup() {
        const route = useRoute()
        const notifications = useNotifications()

        const ltl_rpp_id = Number(route.params.id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_submit: false,
            is_submit_loading: false,
            comment: null as string | null,
            ltlRpp: null as LtlRppDetails | null,
            comments: [] as LtlRppComment[],
            bidComments: [] as LtlRppComment[],
        })

        const loadBidDetail = async () => {
            if (!state.ltlRpp || !state.ltlRpp.tripId) return

            try {
                state.is_loading = true

                const response = await rpp.getLtlRppBid(state.ltlRpp.tripId)
                state.bidComments = response.data
                state.bidComments.forEach((comment) => {
                    state.comments.push({
                        ...comment,
                        stage: 'Заявка логиста',
                    })
                })

                state.comments.reverse()

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadLtlRpp = async () => {
            try {
                state.is_loading = true

                const response = await rpp.getLtlRpp(ltl_rpp_id)
                state.ltlRpp = response.data
                state.comments = [
                    {
                        ...state.ltlRpp.comments,
                        stage: 'РПП',
                    },
                ]

                await loadBidDetail()

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const toggleSubmit = () => {
            state.comment = null
            state.is_submit = !state.is_submit
        }

        const submit = async () => {
            try {
                state.is_submit_loading = true

                const payload = {
                    statusCode: 'confirmed',
                    comment: state.comment || '',
                    paymentBySupplierIds: [ltl_rpp_id],
                }

                await rpp.submitRpp(payload)
                notifications.notify({
                    title: 'РПП успешно подтвержден!',
                    type: 'success',
                })
                toggleSubmit()
                loadLtlRpp()

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При подтверждении РПП произошла ошибка',
                })
            } finally {
                state.is_submit_loading = false
            }
        }

        const isOnEditStatus = computed(() => {
            // 'Подтвержден' или 'К оплате'
            return state.ltlRpp && (state.ltlRpp.status_id === 13 || state.ltlRpp.status_id === 15)
        })

        onBeforeMount(loadLtlRpp)

        return {
            ltl_rpp_id,
            state,
            COLUMNS,
            isOnEditStatus,
            toggleSubmit,
            submit,
            COMMENTS_COLUMNS,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
